/**
 * Datums-Hilfen für die Oberfläche. Alle Daten laufen als ISO-String
 * "JJJJ-MM-TT" — so gibt es keine Zeitzonen-Überraschungen beim Planen.
 */
import { format, parseISO, addDays as addDaysFn, startOfWeek, endOfWeek } from 'date-fns';
import { de } from 'date-fns/locale';

export type RangeMode = 'woche' | 'monat' | 'frei';

export interface DateRange {
  from: string;
  to: string;
}

export function toIso(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function fromIso(iso: string): Date {
  return parseISO(iso);
}

export function todayIso(): string {
  return toIso(new Date());
}

export function addDays(iso: string, days: number): string {
  return toIso(addDaysFn(fromIso(iso), days));
}

/** Kalenderwoche (Mo–So) um ein Datum herum. */
export function weekRange(iso: string): DateRange {
  const date = fromIso(iso);
  return {
    from: toIso(startOfWeek(date, { weekStartsOn: 1 })),
    to: toIso(endOfWeek(date, { weekStartsOn: 1 })),
  };
}

/** Voller Kalendermonat um ein Datum herum. */
export function monthRange(iso: string): DateRange {
  const date = fromIso(iso);
  const year = date.getFullYear();
  const month = date.getMonth();
  return {
    from: toIso(new Date(year, month, 1)),
    to: toIso(new Date(year, month + 1, 0)),
  };
}

export function shiftRange(range: DateRange, mode: RangeMode, direction: -1 | 1): DateRange {
  if (mode === 'monat') {
    const anchor = fromIso(range.from);
    return monthRange(toIso(new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1)));
  }
  if (mode === 'woche') {
    return weekRange(addDays(range.from, 7 * direction));
  }
  // Freier Zeitraum wandert um die eigene Länge weiter.
  const length = dayCount(range);
  return {
    from: addDays(range.from, length * direction),
    to: addDays(range.to, length * direction),
  };
}

export function dayCount(range: DateRange): number {
  const from = fromIso(range.from).getTime();
  const to = fromIso(range.to).getTime();
  return Math.round((to - from) / 86_400_000) + 1;
}

export function eachDay(range: DateRange): string[] {
  const days: string[] = [];
  let current = range.from;
  while (current <= range.to) {
    days.push(current);
    current = addDays(current, 1);
  }
  return days;
}

export function isWithin(iso: string, range: DateRange): boolean {
  return iso >= range.from && iso <= range.to;
}

/** Liegt das Datum innerhalb der Abwesenheit? */
export function overlapsDay(startDate: string, endDate: string, day: string): boolean {
  return startDate <= day && endDate >= day;
}

// --- Anzeigeformate ---

export const formatDay = (iso: string) => format(fromIso(iso), 'd', { locale: de });
export const formatWeekday = (iso: string) => format(fromIso(iso), 'EEEEEE', { locale: de });
export const formatWeekdayLong = (iso: string) => format(fromIso(iso), 'EEEE', { locale: de });
export const formatDate = (iso: string) => format(fromIso(iso), 'dd.MM.yyyy', { locale: de });
export const formatDateShort = (iso: string) => format(fromIso(iso), 'dd.MM.', { locale: de });
export const formatMonthYear = (iso: string) => format(fromIso(iso), 'MMMM yyyy', { locale: de });
export const formatWeekLabel = (iso: string) => `KW ${format(fromIso(iso), 'I', { locale: de })}`;

export function formatRange(range: DateRange, mode: RangeMode): string {
  if (mode === 'monat') return formatMonthYear(range.from);
  if (mode === 'woche') {
    return `${formatWeekLabel(range.from)} · ${formatDateShort(range.from)}–${formatDate(range.to)}`;
  }
  return `${formatDate(range.from)} – ${formatDate(range.to)}`;
}

/** "2 Tage" / "1 Tag" — für Zusammenfassungen. */
export function formatDayCount(count: number): string {
  const rounded = Math.round(count * 10) / 10;
  return `${rounded.toLocaleString('de-DE')} ${rounded === 1 ? 'Tag' : 'Tage'}`;
}

export function formatHours(hours: number): string {
  const sign = hours > 0 ? '+' : '';
  return `${sign}${hours.toLocaleString('de-DE', { maximumFractionDigits: 2 })} h`;
}

export function formatDateTime(value: string): string {
  // SQLite liefert "JJJJ-MM-TT HH:MM:SS" in UTC.
  const normalised = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const date = new Date(normalised);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, 'dd.MM.yyyy, HH:mm', { locale: de });
}

/**
 * Samstag oder Sonntag?
 *
 * Bewusst mit den lokalen Gettern: parseISO liefert Mitternacht **lokaler**
 * Zeit. Mit getUTCDay() laege der Tag in unserer Zeitzone um zwei Stunden
 * zurueck — aus einem Montag wuerde ein Sonntag.
 */
export function isWeekendIso(iso: string): boolean {
  const day = fromIso(iso).getDay();
  return day === 0 || day === 6;
}

/**
 * Kalenderwoche nach ISO 8601 — Woche 1 ist die mit dem ersten Donnerstag.
 * Wichtig zum Jahreswechsel: der 1. Januar liegt oft noch in KW52 oder KW53.
 */
export function weekNumber(iso: string): number {
  const date = fromIso(iso);
  // Auf den Donnerstag derselben Woche schieben, dann das Jahr ablesen.
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const firstThursday = new Date(date.getFullYear(), 0, 4);
  firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7));
  const days = Math.round((date.getTime() - firstThursday.getTime()) / 86_400_000);
  return 1 + Math.round(days / 7);
}
